import { Actors as A, Tenants as T } from '@mntn/auth-types'
import { z } from 'zod'

/** @todo - move these into a shared event registry */
const OfferingPageViewedRequestSchema = z.object({
  name: z.literal('offering page viewed'),
  request: z.object({
    offeringId: z.number().describe('Offering ID'),
  }),
})

const OfferingCardClickedRequestSchema = z.object({
  name: z.literal('offering card clicked'),
  request: z.object({
    offeringId: z.number().describe('Offering ID'),
  }),
})

const FeaturedBannerPurchaseClickedRequestSchema = z.object({
  name: z.literal('featured banner purchase clicked'),
  request: z.object({
    dealName: z.string().describe('Deal name'),
    position: z.number().describe('Slide position'),
  }),
})

const CartIconClickedRequestSchema = z.object({
  name: z.literal('cart icon clicked'),
  request: z.object({
    itemCount: z.number(),
  }),
})

const CampaignSetupButtonClickedRequestSchema = z.object({
  name: z.literal('campaign setup button clicked'),
  request: z.object({
    orderIds: z.array(z.number()),
  }),
})

const CreateCampaignButtonClickedRequestSchema = z.object({
  name: z.literal('create campaign button clicked'),
  request: z.object({
    orderItemId: z.number(),
  }),
})

const PurchaseButtonClickedRequestSchema = z.object({
  name: z.literal('purchase button clicked'),
  request: z.object({}),
})

const AddToCartButtonClickedRequestSchema = z.object({
  name: z.literal('add to cart button clicked'),
  request: z.object({
    offeringId: z.number(),
    offeringVersionId: z.number(),
    impressions: z.number(),
    audienceId: z.number().optional().nullable(),
  }),
})

const OpenAudienceModalButtonClickedRequestSchema = z.object({
  name: z.literal('open audience modal button clicked'),
  request: z.object({
    offeringId: z.number(),
    offeringVersionId: z.number(),
    audienceId: z.number().optional().nullable(),
    mode: z.string(),
  }),
})

const AttachAudienceButtonClickedRequestSchema = z.object({
  name: z.literal('attach audience to order button clicked'),
  request: z.object({
    audienceId: z.number(),
    offeringId: z.number(),
    offeringVersionId: z.number(),
  }),
})

// program page viewed
const ProgramPageViewedRequestSchema = z.object({
  name: z.literal('program page viewed'),
  request: z.object({
    programId: z.number().describe('Program ID'),
  }),
})

// search
const BaseSearch = z.object({
  storeId: z.number().describe('Store ID'),
  searchTerm: z.string().describe('Search Term'),
  searchFilters: z.record(z.string(), z.string()).describe('Filters'),
  searchOrder: z.union([z.literal('asc'), z.literal('desc')]).describe('Sort Order'),
  searchOrderBy: z.string().describe('Sort Order By'),
  searchLocation: z.string().describe('Form Location'),
})

const SearchResultViewedRequestSchema = z.object({
  name: z.literal('search result viewed'),
  request: BaseSearch,
})

// search result clicked
const SearchResultClickedRequestSchema = z.object({
  name: z.literal('search result clicked'),
  request: BaseSearch.extend({
    entityType: z.union([z.literal('program'), z.literal('offering'), z.literal('network')]).describe('Entity Type'),
    entityId: z.number().describe('Entity ID'),
    entityPosition: z.number().describe('Index on the Search Result Page'),
  }),
})

// Flight Deck events
const FlightDeckDashboardViewedRequestSchema = z.object({
  name: z.literal('flight deck dashboard viewed'),
  request: z.object({
    has_purchased_deals: z.boolean(),
    ready_to_create_campaign: z.boolean(),
  }),
})

export const PostEventBodySchema = z.discriminatedUnion('name', [
  AddToCartButtonClickedRequestSchema,
  AttachAudienceButtonClickedRequestSchema,
  CartIconClickedRequestSchema,
  CampaignSetupButtonClickedRequestSchema,
  CreateCampaignButtonClickedRequestSchema,
  FlightDeckDashboardViewedRequestSchema,
  OfferingPageViewedRequestSchema,
  OfferingCardClickedRequestSchema,
  FeaturedBannerPurchaseClickedRequestSchema,
  OpenAudienceModalButtonClickedRequestSchema,
  ProgramPageViewedRequestSchema,
  PurchaseButtonClickedRequestSchema,
  SearchResultViewedRequestSchema,
  SearchResultClickedRequestSchema,
])

export const PostEventResponseSchema = z.object({
  data: z.array(
    z.object({
      ok: z.literal(true),
      name: z.string(),
    }),
  ),
})

export const PostEventMultiStatusResponseSchema = z.object({
  data: z.array(
    z.object({
      ok: z.literal(true),
      name: z.string(),
    }),
  ),
  errors: z.array(
    z.object({
      message: z.string(),
      customCode: z.string(),
    }),
  ),
})

export const PostEventRequestSchema = z.object({
  body: z.custom<z.output<typeof PostEventBodySchema>>(),
  principal: T.KindSchema('advertiser').and(A.UserSchema),
})
